import React, { useState, useEffect } from 'react';
import { useApi } from '../../apiHelpers';
import useMyShowsPrefsStore from '../../stores/useMyShowsPrefsStore';
import { parseDate, formatEventDate, getDisplayStatus } from '../../utils/date-utils';
import dayjs from 'dayjs';
import Panel from '../../components/ui/Panel';
import EmptyState from '../../components/ui/EmptyState';
import { ListStack, ListRow } from '../../components/ui/ListRow';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import FadeIn from '../../components/ui/FadeIn';
import Tag from '../../components/ui/Tag';
import { CATEGORY } from '../../components/ui/categoryColors';

const statusHues = {
  pending: 'neutral',
  confirmed: 'blue',
  packing: 'orange',
  active: 'green',
  completed: 'emerald',
};

const MyShowsModule = () => {
  const api = useApi();
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Set on the My Schedule page; this card only follows it.
  const hidePending = useMyShowsPrefsStore(s => s.hidePending);

  useEffect(() => {
    let current = true;
    const fetch = async () => {
      try {
        const data = await api.get(`/events/my-shows${hidePending ? '?hide_pending=true' : ''}`, { silent: true });
        if (current) setShows(data || []);
      } catch { /* silent */ }
      if (current) setLoading(false);
    };
    fetch();
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidePending]);

  if (loading) {
    return (
      <Panel title="My Upcoming Shows" titleStyle="section" accent="primary" level={3}>
        <SkeletonGroup label="Loading your shows" className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </SkeletonGroup>
      </Panel>
    );
  }

  if (shows.length === 0) {
    return (
      <Panel title="My Upcoming Shows" titleStyle="section" accent="primary" level={3}>
        <EmptyState variant="area">No upcoming shows assigned</EmptyState>
      </Panel>
    );
  }

  return (
    <Panel title="My Upcoming Shows" titleStyle="section" accent="primary" lit level={3}>
      <FadeIn>
      <ListStack
        label="My upcoming shows"
        on="panel"
        items={shows}
        getKey={(item) => String(item.id)}
        renderRow={(show) => {
          const date = parseDate(show.event_date);
          const daysUntil = date.diff(dayjs.utc().startOf('day'), 'day');
          const statusHex = CATEGORY[statusHues[getDisplayStatus(show)] || 'neutral'].ink.hex;

          return (
            <ListRow
              to={`/events/${show.id}`}
              leading={
              <span className="block w-12 text-center">
                {daysUntil <= 0 ? (
                  <span className="block text-[13px] font-bold tracking-wide leading-none" style={{ color: CATEGORY.red.ink.oklch }}>Today</span>
                ) : (
                  <>
                    <span className="block text-lg font-bold tabular-nums" style={{ color: (daysUntil <= 3 ? CATEGORY.red : daysUntil <= 7 ? CATEGORY.orange : CATEGORY.blue).ink.oklch }}>
                      {daysUntil}
                    </span>
                    <span className="block text-[10px] text-on-surface-variant">days</span>
                  </>
                )}
              </span>
              }
              title={show.name}
              meta={[formatEventDate(show.event_date, 'MMM D'), show.location].filter(Boolean).join(', ')}
              trailing={
                <>
                  {show.role && (
                    <span className="text-[10px] font-medium text-primary-ink px-1.5 py-0.5 rounded-full bg-primary/10">
                      {show.role}
                    </span>
                  )}
                  <Tag size="sm" hex={statusHex}>{getDisplayStatus(show)}</Tag>
                </>
              }
            />
          );
        }}
      />
      </FadeIn>
    </Panel>
  );
};

export default MyShowsModule;
